import { useState } from "react";
import {
    processImage,
    extractCandidates,
    extractRegionsFromROI,
    extractTextRegionsFromROI,
} from "../../../services/hobblivision/imageProcessor";
import {
    preprocessOCRRegions,
    summarizeOCRPreprocessing
} from "../../../services/hobblivision/ocrPreprocessor";
import { runOCR } from "../../../services/hobblivision/ocrService";
import {
    detectCollectible
} from "../../../services/hobblivision/detector";
import {
    detectTextRegionsFromROI
} from "../../../services/hobblivision/textRegionDetector";
import { parseOCR } from "../../../services/hobblivision/parser";
import useMatcher from "./useMatcher";

export default function useHobbliVision() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const matcher = useMatcher();
    const scanImage = async (file, userId = null) => {
        const pipeline = [];
        const scanStarted = performance.now();
        const logStage =
            (message) => {

                const elapsed =
                    (
                        performance.now() -
                        scanStarted
                    ).toFixed(0);

                console.log(
                    `%c[HobbliVision +${elapsed}ms] ${message}`,
                    "color: #4caf50; font-weight: bold;"
                );
            };

        try {
            setLoading(true);
            setError(null);

            /*
             * ====================================================
             * SCAN START
             * ====================================================
             */

            logStage("========================================");
            logStage("SCAN STARTED");
            console.log(
                "HobbliVision scan file:",
                file
            );

            /*
             * ====================================================
             * OpenCV
             * ====================================================
             *
             * Intentionally disabled for now.
             *
             * The native V5 detector is currently our
             * working detector.
             */

            logStage(
                "OpenCV disabled. Using native V5 detector."
            );

            pipeline.push({
                stage: "OpenCV",
                status: "disabled",
                reason:
                    "OpenCV temporarily disabled while Web Worker architecture is implemented.",
                timestamp: Date.now()
            });


            /*
             * ====================================================
             * STAGE 1
             * IMAGE PROCESSING
             * ====================================================
             */

            logStage(
                "Starting image processing..."
            );

            pipeline.push({
                stage: "Image Processing",
                status: "processing",
                timestamp: Date.now()
            });

            const imageStarted =
                performance.now();

            const processedImage =
                await processImage(
                    file
                );

            const imageElapsed =
                (
                    performance.now() -
                    imageStarted
                ).toFixed(0);


            console.log(
                `HobbliVision: processImage() completed after ${imageElapsed}ms.`,
                processedImage
            );


            pipeline.push({
                stage: "Image Processing",
                status: "complete",
                timestamp: Date.now()
            });


            logStage(
                "Image processing COMPLETE."
            );


            /*
             * ====================================================
             * STAGE 2
             * DETECTION
             * ====================================================
             */

            logStage(
                "Starting collectible detection..."
            );

            pipeline.push({
                stage: "Detection",
                status: "processing",
                timestamp: Date.now()
            });


            const detectionStarted =
                performance.now();


            const detection =
                await detectCollectible(
                    processedImage
                );


            const detectionElapsed =
                (
                    performance.now() -
                    detectionStarted
                ).toFixed(0);


            console.log(
                `HobbliVision: detectCollectible() completed after ${detectionElapsed}ms.`,
                detection
            );


            pipeline.push({
                stage: "Detection",
                status: "complete",
                result: detection,
                timestamp: Date.now()
            });


            logStage(
                `COLLECTIBLE DETECTION COMPLETE (${detectionElapsed}ms).`
            );


            /*
             * ====================================================
             * STAGE 2.5
             * ROI / CANDIDATE EXTRACTION
             * ====================================================
             *
             * The detector already produces the authoritative
             * cropped collectible image:
             *
             * detection.boundary.croppedImage
             *
             * We keep extractCandidates() for compatibility
             * and debugging, but the detector ROI is now what
             * feeds the downstream region pipeline.
             */

            logStage(
                "Starting ROI extraction..."
            );

            const candidateStarted =
                performance.now();

            const extractedCandidates =
                extractCandidates(
                    processedImage,
                    detection
                );

            const candidateElapsed =
                (
                    performance.now() -
                    candidateStarted
                ).toFixed(0);

            console.log(
                `HobbliVision: extractCandidates() completed after ${candidateElapsed}ms.`,
                extractedCandidates
            );

            const roi =
                extractedCandidates?.[0]?.canvas ||
                null;

            if (roi) {
                console.log(
                    "HobbliVision: Detector ROI available.",
                    {
                        width: roi.width,
                        height: roi.height,
                        aspectRatio:
                            roi.height
                                ? Number(
                                    (
                                        roi.width /
                                        roi.height
                                    ).toFixed(3)
                                )
                                : null
                    }
                );
            } else {
                console.warn(
                    "HobbliVision: Detector did not produce a cropped ROI."
                );
            }

            pipeline.push({
                stage: "ROI Extraction",
                status:
                    roi
                        ? "complete"
                        : "failed",
                result: {
                    available: !!roi,
                    width:
                        roi?.width || 0,
                    height:
                        roi?.height || 0
                },
                timestamp: Date.now()
            });

            logStage(
                roi
                    ? "ROI extraction COMPLETE."
                    : "ROI extraction FAILED."
            );

            /*
             * ====================================================
             * STAGE 3
             * REGION EXTRACTION
             * ====================================================
             *
             * IMPORTANT:
             *
             * Regions are now extracted FROM THE DETECTED ROI,
             * not from the entire photograph.
             */

            logStage(
                "Starting ROI region extraction..."
            );

            const regionStarted =
                performance.now();

            const regions =
                roi
                    ? extractRegionsFromROI(
                        roi,
                        detection?.layout
                    )
                    : {};

            const regionElapsed =
                (
                    performance.now() -
                    regionStarted
                ).toFixed(0);

            console.log(
                `HobbliVision: extractRegionsFromROI() completed after ${regionElapsed}ms.`,
                regions
            );

            const regionNames =
                Object.keys(
                    regions || {}
                );

            pipeline.push({
                stage: "Region Extraction",
                status:
                    regionNames.length > 0
                        ? "complete"
                        : "failed",
                result: {
                    regionCount:
                        regionNames.length,
                    regions:
                        regionNames
                },
                timestamp: Date.now()
            });

            logStage(
                `ROI region extraction COMPLETE (${regionNames.length} regions).`
            );

            /*
             * ====================================================
             * STAGE 3.5
             * TEXT LOCALIZATION
             * ====================================================
             */

            logStage("Starting text-region localization...");
            const textDetectionStarted = performance.now();
            const textDetection =
                roi
                    ? detectTextRegionsFromROI(roi)
                    : { version: "1.0", detected: false, regions: [], diagnostics: {} };

            const textDetectionElapsed =
                (performance.now() - textDetectionStarted).toFixed(0);

            const textRegions =
                roi
                    ? extractTextRegionsFromROI(roi, textDetection)
                    : {};

            const textRegionNames = Object.keys(textRegions);

            console.log(
                `HobbliVision: text-region localization completed after ${textDetectionElapsed}ms.`,
                textDetection,
                textRegions
            );

            pipeline.push({
                stage: "Text Localization",
                status: textRegionNames.length > 0 ? "complete" : "fallback",
                result: {
                    ...textDetection.diagnostics,
                    regionCount: textRegionNames.length,
                    regions: textDetection.regions || []
                },
                timestamp: Date.now()
            });

            logStage(
                `TEXT LOCALIZATION COMPLETE (${textDetectionElapsed}ms, ${textRegionNames.length} regions).`
            );

            /*
            * ====================================================
            * STAGE 4
            * OCR PREPROCESSING
            * ====================================================
            * IMPORTANT:
            * This happens AFTER:
            * detector
            *     ↓
            * ROI
            *     ↓
            * card regions
            * We are now preparing the individual regions
            * specifically for OCR.
            */

            logStage(
                "Starting OCR preprocessing..."
            );

            const ocrPreprocessStarted =
                performance.now();

            const ocrInputRegions =
                textRegionNames.length > 0
                    ? textRegions
                    : regions;

            const ocrPreprocessed =
                Object.keys(ocrInputRegions).length > 0
                    ? preprocessOCRRegions(
                        ocrInputRegions
                    )
                    : {
                        version: "1.0",
                        regions: {}
                    };

            const ocrPreprocessElapsed =
                (
                    performance.now() -
                    ocrPreprocessStarted
                ).toFixed(0);

            const ocrPreprocessSummary =
                summarizeOCRPreprocessing(
                    ocrPreprocessed
                );

            console.log(
                `HobbliVision: OCR preprocessing completed after ${ocrPreprocessElapsed}ms.`,
                ocrPreprocessed
            );

            console.log(
                "HobbliVision OCR preprocessing summary:",
                ocrPreprocessSummary
            );

            pipeline.push({
                stage: "OCR Preprocessing",
                status:
                    Object.keys(
                        ocrPreprocessed.regions || {}
                    ).length > 0
                        ? "complete"
                        : "failed",
                result:
                    ocrPreprocessSummary,
                timestamp: Date.now()
            });

            logStage(
                `OCR preprocessing COMPLETE (${ocrPreprocessElapsed}ms).`
            );

           /*
            * ====================================================
            * STAGE 5
            * OCR
            * ====================================================
            */

            logStage(
                "Starting OCR..."
            );

            pipeline.push({
                stage: "OCR",
                status: "processing",
                timestamp: Date.now()
            });

            const ocrStarted = performance.now();
            const ocrResult = await runOCR(ocrPreprocessed);
            const ocrElapsed =
                (
                    performance.now() -
                    ocrStarted
                ).toFixed(0);

            console.log(
                `HobbliVision: OCR completed after ${ocrElapsed}ms.`,
                ocrResult
            );

            pipeline.push({
                stage: "OCR",
                status: "complete",
                result: ocrResult.summary,
                timestamp: Date.now()
            });

            logStage(
                `OCR COMPLETE (${ocrElapsed}ms).`
            );

            /*
            * ====================================================
            * STAGE 6
            * PARSING
            * ====================================================
            */

            logStage(
                "Starting OCR parsing..."
            );
            pipeline.push({
                stage: "Parsing",
                status: "processing",
                timestamp: Date.now()
            });
            const parserStarted =
                performance.now();
            const parsedResult =
                parseOCR(
                    ocrResult
                );
            const parserElapsed =
                (
                    performance.now() -
                    parserStarted
                ).toFixed(0);
            console.log(
                `HobbliVision: parser completed after ${parserElapsed}ms.`,
                parsedResult
            );

            pipeline.push({
                stage: "Parsing",
                status: "complete",
                result: {
                    cardNumber:
                        parsedResult.cardNumber,
                    itemName:
                        parsedResult.itemName,
                    keywords:
                        parsedResult.keywords,
                    clueCount:
                        parsedResult.clues.length
                },
                timestamp: Date.now()
            });
            logStage(
                `PARSING COMPLETE (${parserElapsed}ms).`
            );

            /*
             * ====================================================
             * STAGE 7
             * MATCHING
             * ====================================================
             */

            logStage(
                "Starting card matching..."
            );

            pipeline.push({
                stage: "Matching",
                status: "processing",
                timestamp: Date.now()
            });

            const matchingStarted =
                performance.now();

            /**
             * ====================================================
             * MATCHER
             * ====================================================
             *
             * useMatcher is responsible for:
             *
             *   1. Obtaining candidate Items
             *   2. Including the user's existing user_items
             *   3. Passing those candidates to matcher.js
             *   4. Returning ranked candidates
             *
             * matcher.js remains database-agnostic.
             *
             * The temporary hard-coded test catalog has now been removed.
             */

            const matchResult =
                await matcher.match(
                    parsedResult,
                    [],
                    userId
                );

            const matchingElapsed =
                (
                    performance.now() -
                    matchingStarted
                ).toFixed(0);

            console.log(
                `HobbliVision: matching completed after ${matchingElapsed}ms.`,
                matchResult
            );

            pipeline.push({
                stage: "Matching",
                status: "complete",
                result: {
                    candidateCount:
                        matchResult?.candidates?.length || 0,

                    bestMatch:
                        matchResult?.bestMatch || null,

                    confidence:
                        matchResult?.confidence || 0,

                    candidateSource:
                        matchResult?.diagnostics?.candidateSource ||
                        "unknown",

                    retrievedCandidateCount:
                        matchResult?.diagnostics?.retrievedCandidateCount ||
                        0
                },
                timestamp: Date.now()
            });

            logStage(
                `MATCHING COMPLETE (${matchingElapsed}ms).`
            );

            const candidates =
                matchResult?.candidates || [];


            /*
             * ====================================================
             * INSPECTOR DATA
             * ====================================================
             */

            const inspector = {
                metadata: {
                    fileName:
                        processedImage.fileName,
                    fileSize:
                        `${Math.round(
                            processedImage.fileSize / 1024
                        )} KB`,
                    width:
                        processedImage.width,
                    height:
                        processedImage.height,
                    orientation:
                        processedImage.orientation
                },


                detection: {
                    detected:
                        !!detection?.boundary?.detected,
                    itemType:
                        detection?.itemType ||
                        "unknown",
                    layout:
                        detection?.layout?.name ||
                        "unknown",
                    confidence:
                        detection?.confidence ??
                        0,
                    classificationConfidence:
                        detection?.classificationConfidence ??
                        0,
                    collectibleAspectRatio:
                        detection?.collectibleAspectRatio ??
                        null,
                    boundingBox:
                        detection?.boundary?.boundingBox ||
                        null
                },

                roi: {
                    available: !!roi,
                    width: roi?.width || 0,
                    height: roi?.height || 0,
                    aspectRatio:
                        roi &&
                        roi.height
                            ? Number(
                                (
                                    roi.width /
                                    roi.height
                                ).toFixed(3)
                            )
                            : null
                },

                regions: {
                count: regionNames.length,
                names: regionNames,
                data: regions
            },

                textRegions: {
                    count: textRegionNames.length,
                    names: textRegionNames,
                    detection: textDetection,
                    data: textRegions
                },

            ocrPreprocessing: {
                version:
                    ocrPreprocessed?.version ||
                    null,
                count:
                    Object.keys(
                        ocrPreprocessed?.regions || {}
                    ).length,
                summary: ocrPreprocessSummary,
                data: ocrPreprocessed?.regions || {}
            },

                pipeline: [
                    {
                        name: "OpenCV",
                        complete: false
                    },

                    {
                        name: "Image Loaded",
                        complete: true
                    },

                    {
                        name: "Detection",
                        complete: !!detection
                    },

                    {
                        name: "ROI Extracted",
                        complete: !!roi
                    },

                    {
                        name: "Regions Extracted",
                        complete: regionNames.length > 0
                    },

                    {
                        name: "OCR Preprocessing",
                        complete:
                            Object.keys(
                                ocrPreprocessed?.regions || {}
                            ).length > 0
                    },

                    {
                        name: "OCR",
                        complete: !!ocrResult
                    },

                    {
                        name: "Parsing",
                        complete: !!parsedResult
                    },

                    {
                        name: "Matching",
                        complete: !!matchResult,
                        confidence:
                            matchResult?.confidence || 0,
                        candidateCount:
                            matchResult?.candidates?.length || 0,
                        retrievedCandidateCount:
                            matchResult?.diagnostics
                                ?.retrievedCandidateCount || 0,
                        candidateSource:
                            matchResult?.diagnostics
                                ?.candidateSource || "unknown"
                    }
                ]
            };

            /*
             * ====================================================
             * PERFORMANCE SUMMARY
             * ====================================================
             */

            const totalElapsed =
                (
                    performance.now() -
                    scanStarted
                ).toFixed(0);

            console.log(
                "HobbliVision performance summary:",
                {
                    totalMs:
                        Number(
                            totalElapsed
                        ),
                    imageProcessingMs:
                        Number(
                            imageElapsed
                        ),
                    detectionMs:
                        Number(
                            detectionElapsed
                        ),
                    candidateExtractionMs:
                        Number(
                            candidateElapsed
                        ),
                    regionExtractionMs:
                        Number(
                            regionElapsed
                        )
                }
            );

            /*
             * ====================================================
             * COMPLETE
             * ====================================================
             */

            logStage(
                `SCAN COMPLETE (${totalElapsed}ms).`
            );

            return {
                processedImage,
                detection,
                boundary:
                    detection?.boundary,
                roi,
                pipeline,
                extractedCandidates,
                regions,
                textDetection,
                textRegions,
                ocrPreprocessed,
                ocrResult,
                parsedResult,
                matchResult,
                candidates,
                inspector
            };
        }

        catch (err) {
            console.error( "========================================" );
            console.error( "HOBBLIVISION SCAN ERROR" );
            console.error( err );
            console.error( "========================================" );
            setError( err );

            pipeline.push({
                stage: "Error",
                status: "failed",
                error: err,
                timestamp: Date.now()
            });

            return {
                processedImage: null,
                detection: null,
                boundary: null,
                roi: null,
                pipeline,
                extractedCandidates: [],
                regions: {},
                textDetection: null,
                textRegions: {},
                ocrPreprocessed: null,
                ocrResult: null,
                parsedResult: null,
                matchResult: null,
                candidates: [],
                inspector: null
            };
        }

        finally {
            logStage( "scanImage() finished." );
            setLoading( false );
        }
    };

    return {
        scanImage,
        loading,
        error
    };
}